import { useEffect, useState } from 'react';
import { useAccount } from '../contexts/AccountContext';
import { apiService } from '../services/api';
import { fetchTenderSections } from '../services/tenderSectionService';
import { PERMISSIONS } from '../shared/constants/permissions';
import type { Assignment } from '../types/assignments';
import type { Section } from '../types/tender';

export const useAssignments = () => {
  const { account, hasPermission } = useAccount();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadAssignments = async () => {
      if (!account?.uid) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch all active tenders the user has access to
        const tenders = await apiService.getTenders({ includeArchived: false });

        const allAssignmentsPromises = tenders.map(
          async (tender: import('../types/tender').Tender) => {
            if (!tender.id) {
              return [];
            }

            try {
              const isOwner = tender.ownerId === account.uid;
              const hasGlobalPerm =
                hasPermission(PERMISSIONS.TENDER_SECTIONS_VIEW_CLIENT) ||
                hasPermission(PERMISSIONS.TENDER_VIEW_CLIENT);
              const isTeamPerm =
                hasPermission(PERMISSIONS.TENDER_MANAGE_TEAM) && tender.teamId === account.teamId;
              const hasMemberPerm = tender.memberPermissions?.[account.uid] !== undefined;
              const isSharedWith = tender.sharedWith?.includes(account.uid) ?? false;

              const canAccessTender =
                isOwner || hasGlobalPerm || isTeamPerm || hasMemberPerm || isSharedWith;

              if (!canAccessTender) {
                return [];
              }

              if (tender.archived || tender.status === 'closed') {
                return [];
              }

              const hasGlobalViewAll = hasPermission(PERMISSIONS.TENDER_SECTIONS_VIEW_CLIENT);
              const hasMemberViewAll =
                tender.memberPermissions?.[account.uid]?.includes(
                  PERMISSIONS.TENDER_SECTIONS_VIEW_CLIENT
                ) ||
                tender.memberPermissions?.[account.uid]?.includes(PERMISSIONS.TENDER_FULL_ACCESS) ||
                false;

              const effectiveViewAll =
                tender.memberPermissions?.[account.uid] !== undefined
                  ? hasMemberViewAll
                  : hasGlobalViewAll;

              const canViewAllSections = isOwner || effectiveViewAll;

              const assignedUserId = canViewAllSections ? undefined : account.uid;

              // Fetch all sections for the tender
              const sections = (await fetchTenderSections(tender.id, assignedUserId)) as Section[];

              // Filter sections that are actually assigned
              const assignedSections = sections.filter((sec) => sec.assignedTo != null);

              if (assignedSections.length === 0) {
                return [];
              }

              const tenderAssignments = await Promise.all(
                assignedSections.map(async (sec) => {
                  let plainText = sec.content || '';

                  if (sec.encrypted_content) {
                    plainText = '[Legacy Encrypted Content - Requires Migration]';
                  }

                  const assignment: Assignment = {
                    id: `${tender.id}_${sec.id}`,
                    tenderId: tender.id || '',
                    tenderName: tender.title || 'Untitled Tender',
                    sectionId: sec.id,
                    sectionTitle: sec.title || 'Untitled Section',
                    assignee: sec.assignedTo,
                    status: sec.status || 'pending',
                    sectionText: plainText,
                  };

                  return assignment;
                })
              );

              return tenderAssignments;
            } catch (err) {
              // biome-ignore lint/suspicious/noConsole: Warn on failed section fetch
              console.error(`Error processing assignments for tender ${tender.id}:`, err);
              return [];
            }
          }
        );

        const nestedAssignments = await Promise.all(allAssignmentsPromises);

        // Flatten array and deduplicate by assignment.id
        const flattened = nestedAssignments.flat();
        const uniqueAssignments = Array.from(
          new Map(
            flattened.map((a: import('../types/assignments').Assignment) => [a.id, a])
          ).values()
        );

        if (isMounted) {
          setAssignments(uniqueAssignments as Assignment[]);
          setLoading(false);
        }
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: Expected error on top-level
        console.error('Failed to load assignments:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch assignments');
          setLoading(false);
        }
      }
    };

    loadAssignments();

    return () => {
      isMounted = false;
    };
  }, [account?.uid, account?.teamId, hasPermission]);

  return { assignments, loading, error };
};
